<template>
  <article class="faq-column">
    <h1>Frequently Asked Questions (FAQs)</h1>
    <p>
      Get answers to some of the most commonly asked questions about electric vehicles (EVs) and how to charge on the
      Electrify America network.
    </p>
    <accordion>
      <!-- Electrify Canada -->
      <accordion-item
        :headerBorder="true"
        title="Electrify Canada"
        :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 0 } }, true)"
      >
        <accordion>
          <accordion-item
            title="What is Electrify Canada?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 0, loadInner: 0 } })"
          >
            <p>
              Electrify Canada is building transformative, Ultra-Fast electric vehicle (EV) direct current (DC) charging
              infrastructure to give Canadians the speed and reliability they need to confidently make the switch to
              electric.
            </p>
          </accordion-item>
          <accordion-item
            title="Can only VWs, Audis, and Porsches use Electrify Canada?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 0, loadInner: 1 } })"
          >
            <p>Any EV with a compatible CCS or CHAdeMO charging inlet can use Electrify Canada.</p>
          </accordion-item>
        </accordion>
      </accordion-item>
      <!-- Public Charging -->
      <accordion-item
        :headerBorder="true"
        title="Public Charging"
        :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1 } }, true)"
      >
        <accordion>
          <accordion-item
            title="How do I locate an Electrify Canada charger?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 0 } })"
          >
            <p>
              To locate an Electrify Canada charger near you, search the online map
              <router-link class="link" to="/locate-charger">here.</router-link> You can also download the
              <router-link :to="{ name: 'mobile-app-en' }" class="link">Electrify Canada app</router-link>. to locate an
              available station, track stations coming soon, and get notifications when they're available.
            </p>
          </accordion-item>
          <accordion-item
            title="Why does the charger display a cost when using my Premium Offer complimentary charging benefit?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 1 } })"
          >
            <p>
              The charger shows the initial price of the charge before any promotions and taxes are applied. At the end
              of your session, you'll see your price following the application of your Premium Offer benefit.
            </p>
          </accordion-item>
          <accordion-item
            title="Why do some chargers say “temporarily reduced”?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 2 } })"
          >
            <p>
              Oftentimes when you encounter chargers delivering reduced power, it means our maintenance team has
              temporarily reduced power on the charger for improvements and updates. While these chargers still operate,
              they function at reduced power levels. We understand the inconvenience of slower charging speeds, and
              we're always working to enhance your experience for faster charging in the future.
            </p>
            <p>
              Please note that not all sites are affected. To see the power level of chargers at a station ahead of
              time, use the Electrify Canada mobile app.
            </p>
            <p>Thank you for your patience and support.</p>
          </accordion-item>
          <accordion-item
            title="What should I do if I encounter a charging error?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 3 } })"
          >
            <p>
              In many cases, charging errors are temporary and can be resolved by stopping and restarting your session.
              Here's what to do:
            </p>
            <ol>
              <li>Manually end your charging session by pressing "Stop" in the mobile app or on the charger.</li>
              <li>
                Unplug the charging cable from your vehicle. If necessary, unlock your electric vehicle before
                unplugging the charging cable.
              </li>
              <li>Reconnect the charging cable and start a new charging session.</li>
              <li>
                If the error persists, we suggest moving to another charger and reporting the issue by calling the
                Contact Center number located above the charger screen.
              </li>
              <li>
                For more detailed instructions on how to charge, visit
                <router-link class="link" :to="{ name: 'charging-your-ev-en' }">How to Charge Your EV</router-link>
              </li>
            </ol>
          </accordion-item>
          <accordion-item
            title="Do I need an Electrify Canada Pass or Pass+ membership to charge?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 4 } })"
          >
            <p>
              No, an Electrify Canada membership is not required, but having one comes with benefits. For example, Pass+
              Members save 20% each time they charge. To learn more about Pass or Pass+ membership benefits,
              <router-link class="link" :to="{ name: 'mobile-app-en' }">click here</router-link>.
            </p>
            <p>
              If you need additional support or need to start a charging session by phone, a toll-free number is listed
              on each charger for your convenience.
            </p>
          </accordion-item>
          <accordion-item
            title="What payment methods are accepted?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 5 } })"
          >
            <p>
              We accept credit or debit card payment directly at the charger using Visa, Mastercard, American Express,
              or Discover. We also accept payment through the Electrify Canada mobile app, apps from other automakers,
              Apple Pay, and Google Wallet.
            </p>
          </accordion-item>
          <accordion-item
            title="Can I use membership cards or apps from other charging networks at Electrify Canada stations?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 6 } })"
          >
            <p>
              No, membership cards or apps from other charging networks cannot be used at Electrify Canada stations.
            </p>
          </accordion-item>
          <HowToStartSession
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 7 } })"
          ></HowToStartSession>
          <accordion-item
            title="If I’m not a member, how do I start a charging session at an Electrify Canada charger?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 8 } })"
            ><p>
              As a guest (without an Electrify Canada account), you can pay by credit card. Every Electrify Canada
              charger is equipped with a credit card reader, to make charging easily available and accessible to all EV
              drivers.
            </p>
            <p>
              First, plug the connector into the vehicle. Follow all steps on the touchscreen before entering a Visa or
              MasterCard credit card for payment. If your card has a chip, please insert it fully into the bottom of the
              reader.
            </p>
            <p>
              A temporary authorization of $60 will be applied to all credit or debit cards, and will be released after
              final billing takes place. The duration of this hold is determined by your card provider.
            </p>
          </accordion-item>
          <accordion-item
            title="What do the labels on the Electrify Canada chargers indicate?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 9 } })"
          >
            <p>
              We’re using charger labels created with help from drivers like you! At the top of each label, you’ll find
              connector icons that match your vehicle’s charging port. At the bottom, lightning bolt icons signify the
              charger’s power—the higher the kilowatt (kW), the greater the power. <br />Our charging stations offer
              three connectors:
            </p>
            <ul style="list-style-type: disc; margin-bottom: 10px">
              <li>Green <strong>Hyper-Fast</strong> labels indicate CCS connectors delivering up to 350 kW</li>
              <li>Teal <strong>Ultra-Fast</strong> labels indicate CCS connectors delivering up to 150 kW.</li>
              <li>Blue <strong>CHAdeMO</strong> labels indicate CHAdeMO connectors delivering up to 50 kW.</li>
            </ul>
            <p>
              <img loading="lazy" :src="require('@/assets/images/Faq/charging-labels.png')" alt="" />
            </p>
            <p>
              <strong> Tip: </strong> Not all EVs can charge at a connector’s maximum power. For example, a Hyper-Fast
              label means the charger offers up to 350 kW for a CCS-compatible EV. If your car is not capable of a 350
              kW maximum charge, the charger automatically supplies the highest power your vehicle requests. Check your
              vehicle’s manufacturer manual to learn your car’s capacity. If you need support at the station, contact
              our Customer Assistance team. We are available 24 hours a day / 7 days a week by dialing the toll-free
              number, <strong>1-833-231-3226</strong>.
            </p>
          </accordion-item>
          <accordion-item
            title="If one connector on a charger is already plugged into a vehicle, can I use the other one?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 10 } })"
            ><p>
              Only one connector can be used per Electrify Canada DC fast charger at a time. If either connector is in
              use, please look for the next nearest available charger at that site to plug in. Each of our chargers has
              two connectors to accommodate vehicles with different charging port placement.
            </p></accordion-item
          >
          <accordion-item
            title="How fast can I charge?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 11 } })"
            ><p>
              Each site offers charging speeds of up to 150 kW or 350 kW for capable vehicles. Depending on a number of
              factors—especially the capabilities of the vehicle—charging speeds could reach up to 4-30 km of range for
              every minute of charging.<sup>1</sup>
            </p>
            <p>
              <small>
                <sup>1</sup>
                Figures based on internal estimates and subject to change. Actual driving range will vary based on, but
                not limited to the following: vehicle type; driving and charging habits; weather and temperature;
                battery age and vehicle condition. Importantly, battery capacity decreases with time and use.</small
              >
            </p></accordion-item
          >
          <accordion-item
            title="What is an idle fee?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 12 } })"
            ><p>
              Drivers who do not unplug and move their vehicle ten minutes after their charging session is complete will
              be charged a $0.40 per-minute idle fee. This fee encourages drivers to move their car promptly so that
              others can use the charger. You can be notified when your charging session is complete by text message
              <sup>2</sup> or see it on the charger screen.
            </p>
            <p>
              <small>
                <sup>2</sup>
                Messaging and data rates may apply.
              </small>
            </p></accordion-item
          >
          <accordion-item
            title="How do I get a receipt?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 13 } })"
            ><p>
              If you have a membership, log in to the Electrify Canada app, go to your Account, and select “Charge
              history.” You can filter your history by date and share it by email. You can also receive your receipt by
              text message, if you provide your mobile phone number at the start of a session. (Messaging and data rates
              may apply.) The total cost will also be displayed on the charging station screen at the end of your
              session.
            </p></accordion-item
          >
          <accordion-item
            title="How can I track the progress of my charging session?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 14 } })"
          >
            <p>
              If you provide your mobile phone number at the start of your session, you can get text message updates to
              see*:
            </p>
            <ul>
              <li>When charging has started</li>
              <li>When charging has slowed</li>
              <li>When charging has stopped</li>
              <li>Your final receipt</li>
            </ul>
            <p>You can also track your session through the Electrify Canada app.</p>
            <p>
              <em>
                Tip: You will only receive text messages from Electrify Canada on the mobile phone number related to
                each individual active charging session.*
              </em>
            </p>
            <p>
              <img loading="lazy" width="200" :src="require('@/assets/images/Faq/faq_en.png')" alt="" />
            </p>
            <small>
              <p>
                <sup>*</sup>
                Messaging and data rates may apply.
              </p>
            </small>
          </accordion-item>
          <accordion-item
            title="What does “state of charge” mean?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 15 } })"
          >
            <p>
              <router-link :to="{ name: 'glossary-en', query: { load: 18 } }" class="link"
                >State of charge, or SOC</router-link
              >
              indicates how “full” your EV’s battery is. When charging begins, most EVs send the charger an initial SOC,
              which is displayed on the charger screen. If available, the SOC will be updated as your car charges,
              though some EVs are not capable of sending our chargers this information.
            </p>
            <p>
              Our chargers will also display the time it takes to reach 80% SOC, if your car transmits that information.
              EVs slow their charging rate as the battery approaches a full charge, which typically occurs around 80%.
              The SOC that this occurs at is often referred to as “bulk charge.” Vehicles will continue charging to 100%
              at this slower rate to preserve your battery life.
            </p>
            <p>
              <img loading="lazy" width="300" :src="require('@/assets/images/Faq/faqs_soc.jpg')" alt="" />
            </p>
          </accordion-item>
          <accordion-item
            title="How do I stop the charging session?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 16 } })"
          >
            <p>
              To end the charging session, just press the “Stop” button at the bottom right of the charger screen. You
              can also stop a charging session through the Electrify Canada app. Select “Stop Charging” on the Current
              Session screen.
            </p>
            <p style="margin-bottom: 0">Tips:</p>
            <ul>
              <li>
                If you do not want to start a charging session, but have already plugged in, you might need to press
                “Cancel” on the charger screen to release the connector from your vehicle.
              </li>
              <li>Depending on your vehicle model, you may also need to unlock your car to remove the connector.</li>
            </ul>
          </accordion-item>
          <accordion-item
            title="What should I do if I get a charging error?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 17 } })"
          >
            <p>
              The screen may ask you to unplug the connector from your vehicle and start a new session by plugging it in
              again. You will need to resubmit payment to begin a new session. If it’s still not working, please move to
              a nearby charger at that location if one is available. The Electrify Canada Customer Assistance team is
              also here to help 24 hours a day, 7 days a week. Dial the toll-free number at 1-833-231-3226.
            </p>
          </accordion-item>
          <accordion-item
            title="Why is my charging slowing down after 80% state of charge?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 18 } })"
          >
            <p>
              Toward the end of your session, you may notice your charging speed (kW) slow down. Many vehicles “bulk
              charge” and slow their charging speed when they reach about 80%*.
            </p>
            <p>
              Because of this, we recommend unplugging your vehicle after bulk charging has occurred. When starting a
              charging session, most EVs share their initial
              <router-link class="link" :to="{ name: 'glossary-en', query: { load: 19 } }"
                >state of charge (SOC)</router-link
              >
              with the charger. SOC indicates how "full" the battery is and can be viewed on the charger screen or in
              the Electrify Canada mobile app.
            </p>
            <p>
              To stay updated on your SOC and other charging details, download the Electrify Canada app from the
              <a
                href="https://apps.apple.com/ca/app/electrify-canada/id1472391910"
                rel="noopener"
                target="_blank"
                class="link"
              >
                Apple App Store
              </a>
              or
              <a
                href="https://play.google.com/store/apps/details?id=com.ec.evowner&hl=en_CA"
                rel="noopener"
                target="_blank"
                class="link"
              >
                Google Play Store </a
              >.
            </p>
            <p>
              *Exact battery percentage may vary. Check with your vehicle’s manufacturer to learn when bulk charge
              occurs for your vehicle.
            </p>
          </accordion-item>
          <accordion-item
            title="Why was I charged $60 at the charging station when I paid with my credit card?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 19 } })"
          >
            <p>
              The $60 you see is a temporary authorization, not an actual charge. Once you are done charging and the
              transaction has settled, the $60 hold is removed and a single charge is processed for the transaction
              cost. Depending on your bank, the hold is typically removed within 24-48 hours once the transaction is
              settled.
            </p>
          </accordion-item>
          <accordion-item
            title="How long will it take to charge my EV at an Electrify Canada station?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 20 } })"
          >
            <p>
              Charging time varies for each session and depends on factors such as your EV type, battery age, and
              weather conditions. Many modern EVs can reach up to an 80% charge in about 30 minutes using our Ultra-Fast
              chargers. Check your owner's manual or specifications of the vehicle online to find your battery capacity
              in kilowatt-hours (kWh). Remember, the higher the kW, the faster the charge.
            </p>
          </accordion-item>
          <accordion-item
            title="Can I use Plug&Charge with my EV at your stations?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 21 } })"
          >
            <p>
              Plug&Charge is now available at all Electrify Canada charging stations. However, it is only available for
              capable EVs.
            </p>
            <p>
              To find out if your EV is Plug&Charge capable, please contact your vehicle’s manufacturer or look at your
              vehicle's owner's manual for more information.
            </p>
          </accordion-item>
          <accordion-item
            title="Can I purchase Electrify Canada stock?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 22 } })"
          >
            <p>No, Electrify Canada is not a publicly traded company.</p>
          </accordion-item>
          <accordion-item
            title="Can I get an RFID card from Electrify Canada?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 23 } })"
          >
            <p>
              Currently, Electrify Canada does not offer RFID cards. To initiate a charge, you can use the Electrify
              Canada mobile app, Apple Wallet, credit card, or Plug&Charge for capable vehicles.
            </p>
          </accordion-item>
          <accordion-item
            title="Why do some of your chargers have two cables?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 24 } })"
          >
            <p>
              Older generations of our chargers feature two cables to accommodate various EV charging port locations and
              ensure easy accessibility for any vehicle.
            </p>
            <p>
              Our next-generation chargers come with a 24-foot cable to help make it easy to extend and adapt to
              different EV charging port setups.
            </p>
            <p>Please note, that for all charger models, only one EV can use the charger at a time.</p>
          </accordion-item>
          <accordion-item
            title="Why does my EV take longer to charge in the cold?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 25 } })"
          >
            <p>
              In colder temperatures, your EV’s software typically lowers the charging power. This is a built-in feature
              in your vehicle to protect the battery.
            </p>
            <p>
              So, when temperatures are colder, it’s important to plan ahead for longer charge times, whether the trip
              is part of your daily commute or a longer road trip. For more information, read our cold weather charging
              tips
              <a href="https://media.electrify-canada.ca/en-ca/releases/30" rel="noopener" target="_blank" class="link">
                here</a
              >.
            </p>
          </accordion-item>
          <accordion-item
            title="Is it okay to park my EV outside in the cold?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 26 } })"
          >
            <p>
              Yes, it is fine to park your EV outside during the colder months. However, keeping your EV in a warmer
              garage can help the battery charge faster and hold its charge longer.
            </p>
          </accordion-item>
          <accordion-item
            title="Are there any tips when charging my EV in colder temperatures?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 1, loadInner: 27 } })"
          >
            <img
              class="image--infograph"
              src="@/assets/images/Faq/faqs_cold_weather_tips_en.webp"
              alt="1. Anticipate longer charging sessions 2. Temperature changes may impact EV Range 3. Plan your charging session 4. Park indoors when you can 5. Utilize your EVs cold weather features"
            />
          </accordion-item>
        </accordion>
      </accordion-item>
      <!-- Membership -->
      <accordion-item
        :headerBorder="true"
        title="Membership"
        :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 2 } }, true)"
      >
        <accordion>
          <accordion-item
            title="Why should I become an Electrify Canada member?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 2, loadInner: 0 } })"
            ><p>
              Electrify Canada Pass and Pass+ members have access to the Electrify Canada mobile app which enables them
              to
            </p>
            <ul>
              <li>See real-time availability of chargers before arriving at a station</li>
              <li>Add favourite stations for faster searching and navigation</li>
              <li>Receive notifications when a charger becomes available</li>
              <li>
                Set state of charge, cost, or time notifications for a charging session (swipe to start sessions only)
              </li>
              <li>Start charging sessions using a smartphone (no more digging around for a credit or debit card)</li>
              <li>Track your vehicle’s charge status if you’re away from it</li>
              <li>Review charging and billing history</li>
            </ul>
          </accordion-item>
          <accordion-item
            :title="$t('differencePassPassPlus')"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 2, loadInner: 1 } })"
          >
            <p>{{ $t('differencePassPassPlusAnswer1') }}</p>
            <ul>
              <li>{{ $t('differencePassPassPlusAnswer2') }}</li>
              <li>{{ $t('differencePassPassPlusAnswer3') }}</li>
            </ul>
            <p>{{ $t('differencePassPassPlusAnswer4') }}</p>
            <ul>
              <li>{{ $t('differencePassPassPlusAnswer5') }}</li>
            </ul>
          </accordion-item>
          <accordion-item
            title="How do I change from one membership type to another?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 2, loadInner: 2 } })"
            ><p>
              In your account, go to Plan details and follow the prompts to change plan types. Please note that a
              request to change from Pass+ to Pass membership will only go into effect on the 1st day of the next
              billing cycle, which coincides with the monthly anniversary of your sign-up date.
            </p>
          </accordion-item>
          <accordion-item
            title="How do I delete my account?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 2, loadInner: 3 } })"
          >
            <p>
              Cancel your membership anytime in the app by navigating to your profile and selecting “Delete Account.”
            </p>
          </accordion-item>
        </accordion>
      </accordion-item>
      <!-- Pricing & Payment -->
      <accordion-item
        :headerBorder="true"
        title="Pricing & Payment"
        :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 3 } }, true)"
      >
        <accordion>
          <HowToPay :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 3, loadInner: 0 } })"></HowToPay>
          <accordion-item
            :headerBorder="true"
            :title="$t('outOfMoney')"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 3, loadInner: 1 } })"
          >
            <p>
              {{ $t('outOfMoneyAnswer') }}
            </p>
          </accordion-item>
          <accordion-item
            :title="$t('determinePricing')"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 3, loadInner: 2 } })"
          >
            <p>
              {{ $t('determinePricingAnswer') }}
            </p>
          </accordion-item>
          <accordion-item
            title="How does auto-reload work during my first charging session?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 3, loadInner: 3 } })"
          >
            <p>
              When you use an Electrify Canada station for the first time, you'll be billed an amount you preselected in
              our mobile app during your initial account setup. This billed amount is held in your account to cover any
              additional costs that aren't included in your plan, such as idle fees and applicable taxes, or for future
              charging sessions. To make sure your charging experience is seamless, your Electrify Canada account will
              automatically reload with the fund amount you preselected when it drops below $5. To change your
              auto-reload amount, visit the Account tab in the Electrify Canada mobile app, where you can select from
              the options $10, $20, $40, or $60.
            </p>
          </accordion-item>
          <accordion-item
            title="Why do I need to set an auto-reload amount when my Premium Offer covers my charging costs?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 3, loadInner: 4 } })"
          >
            <p>Your Premium Offer covers most of the cost of charging but does not cover:</p>
            <ul>
              <li>Idle fees</li>
              <li>Premium offer charging time limitations</li>
            </ul>
            <p>
              The auto-reload amount allows you to cover these costs while charging uninterrupted. Check your vehicle’s
              terms and conditions for the precise details of your Premium Offer.
            </p>
          </accordion-item>
          <accordion-item
            title="How do I enroll in my complimentary premium offer?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 3, loadInner: 5 } })"
          >
            <p>
              The enrollment process varies by vehicle manufacturer. Some vehicle manufacturers use the Electrify Canada
              app while others have their own mobile app to enroll. To determine which app is required for your Premium
              Offer, visit our
              <router-link class="link" :to="{ name: 'premium-offers-en' }"> Premium Offers webpage</router-link>, where
              you can click on your car's manufacturer to find detailed information.
            </p>
            <p>
              For auto manufacturers using the Electrify Canada app, the Premium Offer requires a one-time enrollment.
              Use the enrollment code provided by your dealer to redeem your complimentary charging within the Electrify
              Canada app.
            </p>
            <ol>
              <li>
                Download the Electrify Canada mobile app<reference-link :num="1">
                  Your mobile carrier's messaging, data, and other rates and fees will apply to your use of the
                  Electrify Canada mobile application</reference-link
                >
                and create your account
              </li>
              <li>Select <strong>Premium Offer</strong> in the Account tab</li>
              <li>Tap <strong>I have an enrollment code</strong></li>
              <li>Enter your enrollment code and select your plan</li>
              <li>
                Accept the Plan Disclosure and add a backup payment method for any fees that are not covered by your
                complimentary charging*
              </li>
            </ol>
            <p>
              *To help ensure you can charge uninterrupted, your Electrify Canada account balance automatically reloads
              when it falls below $5. You’ll be billed the auto-reload amount you select in the app, the first time you
              charge at an Electrify Canada station, and the billed amount will be held in your account to cover costs
              not covered by this plan, such as idle fees and applicable taxes. Drivers who do not unplug and move their
              vehicle ten minutes after their charging session is complete will be charged a $0.40 per-minute idle fee.
              This fee encourages drivers to move their car promptly so that others can use the charger. You can be
              notified when your charging session is complete by text message (your mobile carrier’s messaging, data and
              other rates may apply) or see it on the charger screen.
            </p>
          </accordion-item>
        </accordion>
      </accordion-item>
      <!-- Mobile App -->
      <accordion-item
        :headerBorder="true"
        title="Mobile App"
        :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 4 } }, true)"
      >
        <accordion>
          <accordion-item
            title="How do I access my Electrify Canada account?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 4, loadInner: 0 } })"
          >
            <p>
              You'll need our mobile app to create and log in to your Electrify Canada account. Download the Electrify
              Canada app from the
              <a
                href="https://apps.apple.com/ca/app/electrify-canada/id1472391910"
                rel="noopener"
                target="_blank"
                class="link"
              >
                Apple App Store
              </a>
              or
              <a
                href="https://play.google.com/store/apps/details?id=com.ec.evowner&hl=en_CA"
                rel="noopener"
                target="_blank"
                class="link"
              >
                Google Play Store </a
              >. Once downloaded, you can sign up or log in to your account.
            </p>
          </accordion-item>
          <accordion-item
            :title="$t('needNewAccount')"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 4, loadInner: 1 } })"
          >
            <p>{{ $t('needNewAccountAnswer') }}</p>
          </accordion-item>
          <accordion-item
            :title="$t('redesignedApp')"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 4, loadInner: 2 } })"
          >
            <p>
              {{ $t('redesignedAppAnswer') }}
            </p>
          </accordion-item>
          <accordion-item
            :title="$t('homestationWithApp')"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 4, loadInner: 3 } })"
          >
            <p>{{ $t('homestationWithAppAnswer') }}</p>
          </accordion-item>
          <accordion-item
            :title="$t('appToUseHomestation')"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 4, loadInner: 4 } })"
          >
            <p>
              {{ $t('appToUseHomestationAnswer') }}
            </p>
          </accordion-item>
          <accordion-item
            title="What is Multi-Factor Authentication, and how do I verify my account?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 4, loadInner: 5 } })"
          >
            <p>
              Multi-Factor Authentication (MFA) adds an extra layer of security to protect your Electrify Canada
              account. When logging in, you will first enter your username and password, then be prompted to verify your
              identity with a one-time verification code sent to your email.
            </p>
            <p>
              To complete verification, enter the code exactly as shown on the “Verify Your Identity” screen within the
              app and select “Continue.” This process helps prevent unauthorized access to your account.
            </p>
            <p>You can also follow these steps when logging in through the Electrify Canada app:</p>
            <ol>
              <li>Open the Electrify Canada app and log in to your account.</li>
              <li>
                A one-time verification code will be automatically sent to your email associated with your account.
              </li>
              <li>
                Return to the Electrify Canada app and enter the one-time verification code exactly as shown in the
                “Verify Your Identity” screen.
              </li>
              <li>Select “Continue” to complete the verification process.</li>
              <li>Once completed, MFA will be enabled on your account for added security.</li>
            </ol>
          </accordion-item>
          <accordion-item
            title="I cannot access the email associated with my account. How can I complete Multi-Factor Authentication?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 4, loadInner: 6 } })"
          >
            <p>
              If you are unable to access the email associated with your Electrify Canada account, you will need to
              request an account deletion and create a new account with an email you can access.
            </p>
          </accordion-item>
          <accordion-item
            title="I received an email with a Multi-Factor Authentication code to verify my account, but I did not try to log in. What should I do?"
            :onTrigger="() => updateURL({ name: 'faqs-en', query: { loadOuter: 4, loadInner: 7 } })"
          >
            <p>
              If you received a Multi-Factor Authentication (MFA) code but did not attempt to log in, it could indicate
              an unauthorized login attempt. For security, we recommend resetting your password immediately. You can do
              this through the Electrify Canada app or website.
            </p>
          </accordion-item>
        </accordion>
      </accordion-item>
    </accordion>
  </article>
</template>

<script>
import lang from '@/lang';
import Faqs from '@/lang/Faqs';
import Accordion from '@/components/Accordion.vue';
import AccordionItem from '@/components/AccordionItem.vue';
import HowToPay from '@/components/Faqs/HowToPay.vue';
import HowToStartSession from '@/components/Faqs/HowToStartSession.vue';
import ReferenceLink from '../../components/ReferenceLink.vue';

export default {
  name: 'faqs',
  metaInfo: {
    title: 'Frequently asked questions (FAQ) about EV charging',
    link: [
      {
        rel: 'canonical',
        href: 'https://www.electrify-canada.ca/faqs/',
      },
    ],
    meta: [
      {
        name: 'description',
        content:
          'Read our frequently asked questions (FAQs) to learn about charging your electric vehicle (EV) on the Electrify Canada public charging network.',
      },
    ],
    script: [
      {
        type: 'application/ld+json',
        json: {
          '@context': 'http://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What is Electrify Canada?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Electrify Canada is building transformative, Ultra-Fast electric vehicle (EV) direct current (DC) charging infrastructure to give Canadians the speed and reliability they need to confidently make the switch to electric.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Can only VWs, Audis, and Porsches use Electrify Canada?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Any EV with a compatible CCS or CHAdeMO charging inlet can use Electrify Canada.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'How do I locate an Electrify Canada charger?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: `<p>To locate an Electrify Canada charger near you, search the online map here. You can also download the Electrify Canada app. to locate an available station, track stations coming soon, and get notifications when they're available.</p>`,
              },
            },
            {
              '@type': 'Question',
              name: ' How do I use my membership plan to start a charging session at an Electrify Canada charger? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: `<p>As an Electrify Canada Pass or Pass+ member, if you’ve downloaded the Electrify Canada app* and added a valid payment method to your account, you have two ways to start a charge by smartphone:</p><p>1. Using the digital membership pass, which requires tapping or holding the smartphone near the charger’s RFID reader.</p><p>First, plug the connector into the vehicle. Follow all steps on the charger screen before tapping or holding your phone near the reader.</p><p>Android users: Ensure that NFC is turned on in your phone settings and that the smartphone screen is unlocked before tapping the smartphone to the reader—look for the symbol below. The Electrify Canada app does not need to be open.</p><p>iOS users: Open Apple Wallet or the Electrify Canada app. Tap the smartphone to the reader on the right side of the charger—look for the symbol shown here</p><p>2. Using “Swipe to Start Charge” from within the Electrify Canada app.</p><p>Ensure that your smartphone’s location services are enabled, the Electrify Canada app is installed on your phone, and you are logged into your account.</p><p>Open the app and from the map screen (or your Favourites), select the charging station where you are located. You must be near the charger to start a charge.</p><p>Select the charger by referring to the Charger ID number at the top of the charger’s screen, and swipe to start a charging session.</p><p>Hint: Look for the last two digits on the charger your vehicle is plugged into.</p><p>*Your mobile carrier's normal messaging, data, and other rates and fees will apply to your use of the Electrify Canada mobile application</p>`,
              },
            },
            {
              '@type': 'Question',
              name: ' If I’m not a member, how do I start a charging session at an Electrify Canada charger? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>As a guest (without an Electrify Canada account), you can pay by credit card. Every Electrify Canada charger is equipped with a credit card reader, to make charging easily available and accessible to all EV drivers.First, plug the connector into the vehicle. Follow all steps on the touchscreen before entering a Visa or MasterCard credit card for payment. If your card has a chip, please insert it fully into the bottom of the reader. A temporary authorization of $60 will be applied to all credit or debit cards, and will be released after final billing takes place. The duration of this hold is determined by your card provider.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' What do the labels on the Electrify Canada chargers indicate? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>We’re using charger labels created with help from drivers like you! At the top of each label, you’ll find connector icons that match your vehicle’s charging port. At the bottom, lightning bolt icons signify the charger’s power—the higher the kilowatt (kW), the greater the power.</p><p>Our charging stations offer three connectors:</p><p>Green Hyper-Fast labels indicate CCS connectors delivering up to 350 kW</p><p>Teal Ultra-Fast labels indicate CCS connectors delivering up to 150 kW.</p><p>Blue CHAdeMO labels indicate CHAdeMO connectors delivering up to 50 kW.</p><p>Blue label CHAdeMO up to 50 kw, teal label CCS Ultra-Fast up to 150 kw, green label CCS Hyper-Fast up to 350 kw</p><p>Tip: Not all EVs can charge at a connector’s maximum power. For example, a Hyper-Fast label means the charger offers up to 350 kW for a CCS-compatible EV. If your car is not capable of a 350 kW maximum charge, the charger automatically supplies the highest power your vehicle requests. Check your vehicle’s manufacturer manual to learn your car’s capacity. If you need support at the station, contact our Customer Assistance team. We are available 24 hours a day / 7 days a week by dialing the toll-free number, 1-833-231-3226.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' If one connector on a charger is already plugged into a vehicle, can I use the other one? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Only one connector can be used per Electrify Canada DC fast charger at a time. If either connector is in use, please look for the next nearest available charger at that site to plug in. Each of our chargers has two connectors to accommodate vehicles with different charging port placement.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' How fast can I charge? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Each site offers charging speeds of up to 150 kW or 350 kW for capable vehicles. Depending on a number of factors—especially the capabilities of the vehicle—charging speeds could reach up to 4-30 km of range for every minute of charging.1</p><p>1 Figures based on internal estimates and subject to change. Actual driving range will vary based on, but not limited to the following: vehicle type; driving and charging habits; weather and temperature; battery age and vehicle condition. Importantly, battery capacity decreases with time and use.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Which payment methods are accepted? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>All Electrify Canada chargers accept Visa and MasterCard credit cards and debit cards.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' What is an idle fee? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Drivers who do not unplug and move their vehicle ten minutes after their charging session is complete will be charged a $0.40 per-minute idle fee. This fee encourages drivers to move their car promptly so that others can use the charger. You can be notified when your charging session is complete by text message 2 or see it on the charger screen.</p><p>2 Messaging and data rates may apply.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' How do I get a receipt? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>If you have a membership, log in to the Electrify Canada app, go to your Account, and select “Charge history.” You can filter your history by date and share it by email. You can also receive your receipt by text message, if you provide your mobile phone number at the start of a session. (Messaging and data rates may apply.) The total cost will also be displayed on the charging station screen at the end of your session.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' How can I track the progress of my charging session? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>If you provide your mobile phone number at the start of your session, you can get text message updates to see*:</p><p>When charging has started</p><p>When charging has slowed</p><p>When charging has stopped</p><p>Your final receipt</p><p>You can also track your session through the Electrify Canada app.</p><p>Tip: You will only receive text messages from Electrify Canada on the mobile phone number related to each individual active charging session.*</p><p>* Messaging and data rates may apply.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' What does “state of charge” mean? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>State of charge, or SOC indicates how “full” your EV’s battery is. When charging begins, most EVs send the charger an initial SOC, which is displayed on the charger screen. If available, the SOC will be updated as your car charges, though some EVs are not capable of sending our chargers this information.</p><p>Our chargers will also display the time it takes to reach 80% SOC, if your car transmits that information. EVs slow their charging rate as the battery approaches a full charge, which typically occurs around 80%. The SOC that this occurs at is often referred to as “bulk charge.” Vehicles will continue charging to 100% at this slower rate to preserve your battery life.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' How do I stop the charging session? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>To end the charging session, just press the “Stop” button at the bottom right of the charger screen. You can also stop a charging session through the Electrify Canada app. Select “Stop Charging” on the Current Session screen.</p><p>Tips:</p><p>If you do not want to start a charging session, but have already plugged in, you might need to press “Cancel” on the charger screen to release the connector from your vehicle.</p><p>Depending on your vehicle model, you may also need to unlock your car to remove the connector.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' What should I do if I get a charging error? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>The screen may ask you to unplug the connector from your vehicle and start a new session by plugging it in again. You will need to resubmit payment to begin a new session. If it’s still not working, please move to a nearby charger at that location if one is available. The Electrify Canada Customer Assistance team is also here to help 24 hours a day, 7 days a week. Dial the toll-free number at 1-833-231-3226.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Why is my charging slowing down after 80% state of charge?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Toward the end of your session, you may notice your charging speed (kW) slow down. Many vehicles “bulk charge” and slow their charging speed when they reach about 80%*.</p><p>Because of this, we recommend unplugging your vehicle after bulk charging has occurred. When starting a charging session, most EVs share their initial <router-link class="link" :to="{ name: \'glossary-en\', query: { load: 18 } }">state of charge (SOC)</router-link> with the charger. SOC indicates how "full" the battery is and can be viewed on the charger screen or in the Electrify Canada mobile app.</p><p>To stay updated on your SOC and other charging details, download the Electrify Canada app from the <a href="https://apps.apple.com/ca/app/electrify-canada/id1472391910" rel="noopener" target="_blank" class="link">Apple App Store</a> or <a href="https://play.google.com/store/apps/details?id=com.ec.evowner&hl=en_CA" rel="noopener" target="_blank" class="link">Google Play Store</a>.</p><p>*Exact battery percentage may vary. Check with your vehicle’s manufacturer to learn when bulk charge occurs for your vehicle.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Why was I charged $60 at the charging station when I paid with my credit card?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>The $60 you see is a temporary authorization, not an actual charge. Once you are done charging and the transaction has settled, the $60 hold is removed and a single charge is processed for the transaction cost. Depending on your bank, the hold is typically removed within 24-48 hours once the transaction is settled.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'How long will it take to charge my EV at an Electrify Canada station?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: "<p>Charging time varies for each session and depends on factors such as your EV type, battery age, and weather conditions. Many modern EVs can reach up to an 80% charge in about 30 minutes using our Ultra-Fast chargers. Check your owner's manual or specifications of the vehicle online to find your battery capacity in kilowatt-hours (kWh). Remember, the higher the kW, the faster the charge.</p>",
              },
            },
            {
              '@type': 'Question',
              name: 'Can I use Plug&Charge with my EV at your stations?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: "<p>Plug&Charge is now available at all Electrify Canada charging stations. However, it is only available for capable EVs.</p><p>To find out if your EV is Plug&Charge capable, please contact your vehicle’s manufacturer or look at your vehicle's owner's manual for more information.</p>",
              },
            },
            {
              '@type': 'Question',
              name: 'Can I purchase Electrify Canada stock?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>No, Electrify Canada is not a publicly traded company.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Can I get an RFID card from Electrify Canada?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Currently, Electrify Canada does not offer RFID cards. To initiate a charge, you can use the Electrify Canada mobile app, Apple Wallet, credit card, or Plug&Charge for capable vehicles.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Why do some of your chargers have two cables?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Older generations of our chargers feature two cables to accommodate various EV charging port locations and ensure easy accessibility for any vehicle.</p><p>Our next-generation chargers come with a 24-foot cable to help make it easy to extend and adapt to different EV charging port setups.</p><p>Please note, that for all charger models, only one EV can use the charger at a time.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Why does my EV take longer to charge in the cold?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>In colder temperatures, your EV’s software typically lowers the charging power. This is a built-in feature in your vehicle to protect the battery.</p><p>So, when temperatures are colder, it’s important to plan ahead for longer charge times, whether the trip is part of your daily commute or a longer road trip. For more information, read our cold weather charging tips <a href="https://media.electrify-canada.ca/en-ca/releases/30" rel="noopener" target="_blank" class="link">here</a>.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Is it okay to park my EV outside in the cold?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Yes, it is fine to park your EV outside during the colder months. However, keeping your EV in a warmer garage can help the battery charge faster and hold its charge longer.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Why should I become an Electrify Canada member? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Electrify Canada Pass and Pass+ members have access to the Electrify Canada mobile app which enables them to</p><p>See real-time availability of chargers before arriving at a station</p><p>Add favourite stations for faster searching and navigation</p><p>Receive notifications when a charger becomes available</p><p>Set state of charge, cost, or time notifications for a charging session (swipe to start sessions only)</p><p>Start charging sessions using a smartphone (no more digging around for a credit or debit card)</p><p>Track your vehicle’s charge status if you’re away from it</p><p>Review charging and billing history</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' What are the differences between Pass and Pass+ membership? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Electrify Canada Pass members enjoy all of the benefits above, and</p><p>$0 due at signup and no monthly fee</p><p>Enjoy convenient charging access via your smart phone</p><p>Electrify Canada Pass+ members enjoy all of the benefits above, and</p><p>Pay a monthly fee and enjoy savings of up to 20% on charging.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' How do I change from one membership type to another? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>In your account, go to Plan details and follow the prompts to change plan types. Please note that a request to change from Pass+ to Pass membership will only go into effect on the 1st day of the next billing cycle, which coincides with the monthly anniversary of your sign-up date.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' How do I delete my account? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Cancel your membership anytime in the app by navigating to your profile and selecting “Delete Account.”</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' How do I pay for charging and for the Pass+ subscription fee? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>When you sign up for Electrify Canada Pass or Pass+ in the mobile app, follow the prompts to add your credit card to your account. Then select an amount to automatically load money to your Electrify Canada balance.</p><p>This amount will be added to your account the first time you charge your vehicle at an Electrify Canada charging station after signing up. Every time your balance falls to or below $5, your account will reload with the amount you selected.</p><p>The cost for charging, idling, and any applicable taxes will be deducted from your balance at the end of your charging session.</p><p>If you’re a Pass+ member, your monthly subscription fee will be charged to your credit or debit card when you sign up and deducted from your Electrify Canada balance monthly thereafter.</p><p>Any remaining account balance will automatically be applied to your charging session.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' What happens if I run out of money in my Electrify Canada mobile app wallet? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>To ensure you can seamlessly charge your vehicle at an Electrify Canada charging station, every time your wallet balance falls to or below the minimum wallet threshold, we will automatically charge your card on file to auto-fill your wallet in the amount you select. If your payment method is declined during a charging session, please update your payment method on your account to allow Electrify Canada to auto-fill your wallet so that vehicle charging can continue. If the payment method is not updated, charging will stop when your wallet balance reaches $0.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' How is Electrify Canada DC Fast charging pricing determined? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>You pay for the energy your vehicle receives, regardless of temperature, your battery’s capacity, the time it takes for your vehicle to charge, or other variable factors.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'How does auto-reload work during my first charging session?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: "<p>When you use an Electrify Canada station for the first time, you'll be billed an amount you preselected in our mobile app during your initial account setup. This billed amount is held in your account to cover any additional costs that aren't included in your plan, such as idle fees and applicable taxes, or for future charging sessions. To make sure your charging experience is seamless, your Electrify Canada account will automatically reload with the fund amount you preselected when it drops below $5. To change your auto-reload amount, visit the Account tab in the Electrify Canada mobile app, where you can select from the options $10, $20, $40, or $60.</p>",
              },
            },
            {
              '@type': 'Question',
              name: 'Why do I need to set an auto-reload amount when my Premium Offer covers my charging costs?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Your Premium Offer covers most of the cost of charging but does not cover:</p><ul><li>Idle fees</li><li>Premium offer charging time limitations</li></ul><p>The auto-reload amount allows you to cover these costs while charging uninterrupted. Check your vehicle’s terms and conditions for the precise details of your Premium Offer.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'How do I enroll in my complimentary premium offer?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>The enrollment process varies by vehicle manufacturer. Some vehicle manufacturers use the Electrify Canada app while others have their own mobile app to enroll. To determine which app is required for your Premium Offer, visit our <router-link class="link" :to="{ name: \'premium-offers-en\' }"> Premium Offers webpage</router-link>, where you can click on your car\'s manufacturer to find detailed information.</p><p>For auto manufacturers using the Electrify Canada app, the Premium Offer requires a one-time enrollment. Use the enrollment code provided by your dealer to redeem your complimentary charging within the Electrify Canada app.</p><ol><li>Download the Electrify Canada mobile app<reference-link :num="1">Your mobile carrier\'s messaging, data, and other rates and fees will apply to your use of the Electrify Canada mobile application</reference-link> and create your account</li><li>Select <strong>Premium Offer</strong> in the Account tab</li><li>Tap <strong>I have an enrollment code</strong></li><li>Enter your enrollment code and select your plan</li><li>Accept the Plan Disclosure and add a backup payment method for any fees that are not covered by your complimentary charging*</li></ol><p>*To help ensure you can charge uninterrupted, your Electrify Canada account balance automatically reloads when it falls below $5. You’ll be billed the auto-reload amount you select in the app, the first time you charge at an Electrify Canada station, and the billed amount will be held in your account to cover costs not covered by this plan, such as idle fees and applicable taxes. Drivers who do not unplug and move their vehicle ten minutes after their charging session is complete will be charged a $0.40 per-minute idle fee. This fee encourages drivers to move their car promptly so that others can use the charger. You can be notified when your charging session is complete by text message (your mobile carrier’s messaging, data and other rates may apply) or see it on the charger screen.</p>',
              },
            },

            {
              '@type': 'Question',
              name: ' Will I need to create a new account to use the new app? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>No, you can use your existing Electrify Canada account credentials to sign in to the new app.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' How does the redesigned app make charging at home easier? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>The app will allow you to connect to your Electrify HomeStation™ charger. You’ll be able to start and stop charging, as well as set up a schedule for charging your EV.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'How do I access my Electrify Canada account?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>You\'ll need our mobile app to create and log in to your Electrify Canada account. Download the Electrify Canada app from the <a href="https://apps.apple.com/ca/app/electrify-canada/id1472391910" rel="noopener" target="_blank" class="link">Apple App Store</a> or <a href="https://play.google.com/store/apps/details?id=com.ec.evowner&hl=en_CA" rel="noopener" target="_blank" class="link">Google Play Store</a>. Once downloaded, you can sign up or log in to your account.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Why does the charger display a cost when using my Premium Offer complimentary charging benefit?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: "<p>The charger shows the initial price of the charge before any promotions and taxes are applied. At the end of your session, you'll see your price following the application of your Premium Offer benefit.</p>",
              },
            },
            {
              '@type': 'Question',
              name: 'Why do some chargers say “temporarily reduced”?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: "<p>Oftentimes when you encounter chargers delivering reduced power, it means our maintenance team has temporarily reduced power on the charger for improvements and updates. While these chargers still operate, they function at reduced power levels. We understand the inconvenience of slower charging speeds, and we're always working to enhance your experience for faster charging in the future.</p><p>Please note that not all sites are affected. To see the power level of chargers at a station ahead of time, use the Electrify Canada mobile app.</p><p>Thank you for your patience and support.</p>",
              },
            },
            {
              '@type': 'Question',
              name: 'What should I do if I encounter a charging error?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>In many cases, charging errors are temporary and can be resolved by stopping and restarting your session. Here\'s what to do:</p><ol><li>Manually end your charging session by pressing "Stop" in the mobile app or on the charger.</li><li>Unplug the charging cable from your vehicle. If necessary, unlock your electric vehicle before unplugging the charging cable.</li><li>Reconnect the charging cable and start a new charging session.</li><li>If the error persists, we suggest moving to another charger and reporting the issue by calling the Contact Center number located above the charger screen.</li><li>For more detailed instructions on how to charge, visit <router-link class="link" :to="{ name: \'charging-your-ev-en\' }">How to Charge Your EV</router-link></li></ol>',
              },
            },
            {
              '@type': 'Question',
              name: 'Do I need an Electrify Canada Pass or Pass+ membership to charge?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>No, an Electrify Canada membership is not required, but having one comes with benefits. For example, Pass+ Members save 20% each time they charge. To learn more about Pass or Pass+ membership benefits, <router-link class="link" :to="{ name: \'mobile-app-en\' }">click here</router-link>.</p><p>If you need additional support or need to start a charging session by phone, a toll-free number is listed on each charger for your convenience.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'What payment methods are accepted?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>We accept credit or debit card payment directly at the charger using Visa, Mastercard, American Express, or Discover. We also accept payment through the Electrify Canada mobile app, apps from other automakers, Apple Pay, and Google Wallet.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'Can I use membership cards or apps from other charging networks at Electrify Canada stations?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>No, membership cards or apps from other charging networks cannot be used at Electrify Canada stations.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Does the HomeStation™ work with the Electrify Canada mobile app? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Yes. The Electrify Canada app is available for iOS and Android.</p>',
              },
            },
            {
              '@type': 'Question',
              name: ' Do I need to have the Electrify Canada app to use HomeStation™? ',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>No, the Electrify Canada app is not required to use your HomeStation™. However, in order to experience all features of the HomeStation™, the app is required.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'What is Multi-Factor Authentication, and how do I verify my account?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>Multi-Factor Authentication (MFA) adds an extra layer of security to protect your Electrify Canada account. When logging in, you will first enter your username and password, then be prompted to verify your identity with a one-time verification code sent to your email.</p> <p> To complete verification, enter the code exactly as shown on the “Verify Your Identity” screen within the app and select “Continue.” This process helps prevent unauthorized access to your account.</p><p>You can also follow these steps when logging in through the Electrify Canada app:</p><ol><li>Open the Electrify Canada app and log in to your account.</li><li>A one-time verification code will be automatically sent to your email associated with your account.</li><li>Return to the Electrify Canada app and enter the one-time verification code exactly as shown in the “Verify Your Identity” screen.  </li><li>Select “Continue” to complete the verification process.</li> <li>Once completed, MFA will be enabled on your account for added security.</li></ol>',
              },
            },
            {
              '@type': 'Question',
              name: 'I cannot access the email associated with my account. How can I complete Multi-Factor Authentication?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>If you are unable to access the email associated with your Electrify Canada account, you will need to request an account deletion and create a new account with an email you can access.</p>',
              },
            },
            {
              '@type': 'Question',
              name: 'I received an email with a Multi-Factor Authentication code to verify my account, but I did not try to log in. What should I do?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '<p>If you received a Multi-Factor Authentication (MFA) code but did not attempt to log in, it could indicate an unauthorized login attempt. For security, we recommend resetting your password immediately. You can do this through the Electrify Canada app or website.</p>',
              },
            },
          ],
        },
      },
    ],
  },
  components: {
    AccordionItem,
    Accordion,
    HowToPay,
    HowToStartSession,
    ReferenceLink,
  },
  i18n: {
    messages: Faqs,
    sharedMessages: lang,
  },
  computed: {
    isMobile() {
      return this.$resize && this.$mq.below(1130);
    },
    loadOuter() {
      return this.$route.query.loadOuter;
    },
    loadInner() {
      return this.$route.query.loadInner;
    },
  },
  methods: {
    updateURL(data, isParent = false) {
      const routeParams = this.$route.query;
      const dataParams = data.query;

      const changeInParams =
        parseInt(routeParams.loadInner, 10) !== parseInt(dataParams.loadInner, 10) ||
        parseInt(routeParams.loadOuter, 10) !== parseInt(dataParams.loadOuter, 10);

      const changeInOuterParams = parseInt(routeParams.loadOuter, 10) !== parseInt(dataParams.loadOuter, 10);

      if ((isParent && changeInOuterParams) || changeInParams) {
        this.$router.replace(data);
      }
    },
    scrollToQuestion() {
      // PARENT ACCORDION
      const parentAccordion = document.querySelector('.accordion');
      const parentAccordionId = parentAccordion.id.split('-')[0];
      // Get all the items in the parent accordion by filtering out nested accordion content
      const parentItems = Array.from(parentAccordion?.querySelectorAll('[class~=accordion-trigger]')).filter(
        (item) => !item.closest('.accordion-content')
      );

      if (this.loadOuter >= 0 && this.loadInner >= 0) {
        const targetParentItem = parentItems[this.loadOuter];
        const targetParentItemId = targetParentItem?.id.split('-')[0];

        // CHILD ACCORDION
        const childAccordion = targetParentItem?.nextElementSibling.querySelector('.accordion');
        const childAccordionId = childAccordion?.id.split('-')[0];

        const childItems = childAccordion?.querySelectorAll('[class~=accordion-trigger]');
        const targetChildItem = childItems[this.loadInner];
        const targetChildItemId = targetChildItem?.id.split('-')[0];

        // Trigger parent and child accordion items to open
        this.$root.$emit('toggleAccordion', {
          accordionID: parentAccordionId,
          uniqueID: targetParentItemId,
        });

        this.$root.$emit('toggleAccordion', {
          accordionID: childAccordionId,
          uniqueID: targetChildItemId,
        });

        // Scroll open item into view
        setTimeout(() => {
          const el = document.getElementById(targetChildItem?.id);
          el?.scrollIntoView({ behavior: 'smooth' });
        }, 500);
      } else if (this.loadOuter >= 0) {
        const targetParentItem = parentItems[this.loadOuter];
        const targetParentItemId = targetParentItem?.id.split('-')[0];
        // Trigger parent and child accordion items to open
        this.$root.$emit('toggleAccordion', {
          accordionID: parentAccordionId,
          uniqueID: targetParentItemId,
        });

        // Scroll open item into view
        setTimeout(() => {
          const el = document.getElementById(targetParentItem?.id);
          el?.scrollIntoView({ behavior: 'smooth' });
        }, 500);
      }
    },
  },
  mounted() {
    this.scrollToQuestion();
  },
  watch: {
    $route() {
      // Prevents the page from scrolling to the top when the query parameters in the URL get updated (default behaviour of Vue router on route change)
      this.scrollToQuestion();
    },
  },
};
</script>
